import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserPlan } from '../models/plan';
import { UserSettings, UserMode } from '../models/user';

export interface CurrentUser {
  userId: string;
  username: string;
  firstName: string;
  lastName: string;
  companyName: string;
  token: string;
  plan?: UserPlan;
  settings?: UserSettings;
  trialStatusTypeId?: number | null;
  trialStatusName?: string | null;
  trialExpiresAt?: string | null;
  message?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<CurrentUser | null>(null);
  public currentUser$: Observable<CurrentUser | null> = this.currentUserSubject.asObservable();

  constructor() {
    // Check if user is already logged in on service initialization
    this.loadUserFromStorage();
  }

  /**
   * Set the current authenticated user
   * @param user Current user information
   */
  setCurrentUser(user: CurrentUser): void {
    this.currentUserSubject.next(user);
    this.saveUserToStorage(user);
  }

  /**
   * Update the user's plan information
   * @param plan User's subscription plan
   */
  setUserPlan(plan: UserPlan): void {
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, plan };
      this.currentUserSubject.next(updatedUser);
      this.saveUserToStorage(updatedUser);
    }
  }

  /**
   * Update the user's settings
   * @param settings User's settings
   */
  setUserSettings(settings: UserSettings): void {
    const currentUser = this.getCurrentUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, settings };
      this.currentUserSubject.next(updatedUser);
      this.saveUserToStorage(updatedUser);
    }
  }

  /**
   * Get the current user's settings
   * @returns UserSettings or null
   */
  getUserSettings(): UserSettings | null {
    const user = this.getCurrentUser();
    return user?.settings || null;
  }

  /**
   * Get the current user's mode
   * @returns UserMode or Simple as default
   */
  getUserMode(): UserMode {
    const settings = this.getUserSettings();
    return settings?.userMode || UserMode.Simple;
  }

  /**
   * Get the current user value (synchronous)
   * @returns Current user or null
   */
  getCurrentUser(): CurrentUser | null {
    return this.currentUserSubject.value;
  }

  /**
   * Check if user is authenticated
   * @returns boolean
   */
  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  /**
   * Get the current auth token
   * @returns string or null
   */
  getToken(): string | null {
    const user = this.getCurrentUser();
    return user ? user.token : null;
  }

  /**
   * Logout the current user
   */
  logout(): void {
    this.currentUserSubject.next(null);
    this.clearUserFromStorage();
  }

  /**
   * Save user to localStorage
   * @param user Current user
   */
  private saveUserToStorage(user: CurrentUser): void {
    localStorage.setItem('currentUser', JSON.stringify(user));
  }

  /**
   * Load user from localStorage
   */
  private loadUserFromStorage(): void {
    const savedUser = localStorage.getItem('currentUser');
    if (savedUser) {
      try {
        const user: CurrentUser = JSON.parse(savedUser);
        this.currentUserSubject.next(user);
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        this.clearUserFromStorage();
      }
    }
  }

  /**
   * Clear user from localStorage
   */
  private clearUserFromStorage(): void {
    localStorage.removeItem('currentUser');
    localStorage.removeItem('authToken'); // Clean up old token storage too
  }
}
